import { FileMosaic } from "@files-ui/react";
import he from "he";
import parse from "html-react-parser";
import { useEffect, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import {
  LuChevronDown,
  LuCopy,
  LuLoaderCircle,
  LuPalette,
  LuRotateCcw,
  LuTriangleAlert,
} from "react-icons/lu";
import { Tooltip } from "react-tooltip";
import * as SharedMessageTypes from "../../../../../darvin-shared/schema/zod/agent/message-types.schema";
import { colors, typography, effects } from "../../../../design/tokens";
import { designLibrary } from "../../../../data/designLibrary";
import { useImagePreview } from "../../../../providers/ImagePreviewProvider";
import { useSnackbar } from "../../../../providers/SnackbarProvider";

// Helper function to extract design name from message markers
function getDesignNameFromMessage(message: string): string | null {
  if (!message.includes("[[DESIGN_START]]") || !message.includes("[[DESIGN_END]]")) {
    return null;
  }

  const startMarker = "[[DESIGN_START]]\n";
  const endMarker = "\n[[DESIGN_END]]";
  const startMarkerIndex = message.indexOf(startMarker);
  const endIndex = message.indexOf(endMarker);

  if (startMarkerIndex === -1 || endIndex === -1) {
    return null;
  }

  const startIndex = startMarkerIndex + startMarker.length;
  if (startIndex >= endIndex) {
    return null;
  }

  const promptText = message.substring(startIndex, endIndex);
  const matchedDesign = designLibrary.find(d => d.promptText === promptText);
  return matchedDesign?.name ?? "Custom Design";
}

export interface RollbackBtnProps {
  isDisabled: boolean;
  onClick: () => void;
}

export function ProjectUserMessage({
  message,
  attachments,
  canBeAnimated,
  rollbackBtn,
}: {
  message: string;
  attachments: SharedMessageTypes.UserMessageAttachment[];
  canBeAnimated: boolean;
  rollbackBtn?: RollbackBtnProps;
}) {
  const { openPreview } = useImagePreview();
  const { showSnackbar } = useSnackbar();

  const [mainMessage, setMainMessage] = useState<string | null>(null);
  const [expandableMessage, setExpandableMessage] = useState<string | null>(
    null
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [designName, setDesignName] = useState<string | null>(null);

  useEffect(() => {
    let processedMessage = message;

    // Extract design name before removing markers
    setDesignName(getDesignNameFromMessage(message));

    // Handle design prompt hiding
    if (
      processedMessage.includes("[[DESIGN_START]]") &&
      processedMessage.includes("[[DESIGN_END]]")
    ) {
      // Remove design prompt section entirely from display
      const beforeDesign = processedMessage.split("[[DESIGN_START]]")[0];
      const afterDesign = processedMessage.split("[[DESIGN_END]]")[1] || "";
      processedMessage = (beforeDesign + afterDesign).trim();
    }

    // Handle error details (existing functionality)
    if (
      processedMessage.includes("@@error_details@@") &&
      processedMessage.includes("@@/error_details@@")
    ) {
      setMainMessage(processedMessage.split("@@error_details@@")[0]);
      setExpandableMessage(
        he.encode(
          processedMessage.split("@@error_details@@")[1].split("@@/error_details@@")[0]
        )
      );
    } else {
      setMainMessage(processedMessage);
    }
  }, [message]);

  return (
    <div
      className={`${
        canBeAnimated && "message-container user-container"
      } w-full flex flex-col items-end gap-2 ${rollbackBtn ? "mb-2" : ""}`}
    >
      <div className="flex flex-row flex-wrap gap-2 items-center justify-end">
        {/* Design library indicator */}
        {designName && (
          <div className={`flex items-center gap-2 px-3 py-1.5
            bg-dark-surface-secondary
            border border-dark-border
            rounded-md
            ${typography.size.sm} ${colors.text.primary}`}
          >
            <LuPalette size={16} className={`${colors.text.secondary} opacity-80`} />
            <span>{designName}</span>
          </div>
        )}
        {attachments.map((a, index) =>
          a.mimeType.startsWith("image/") ? (
            <LazyImage
              key={index}
              attachment={a}
              onClick={() => openPreview(a)}
            />
          ) : (
            <File
              key={index}
              id={a.filename}
              type={a.mimeType}
              name={a.filename}
            />
          )
        )}
      </div>
      <div
        className={`max-w-[80%] py-3 px-5 ${rollbackBtn ? "pb-4" : ""}
        bg-dark-surface-tertiary
        ${effects.borderRadius.lg}
        break-words hyphens-auto message-bubble
        ${typography.size.sm} leading-[1.6]`}
      >
        <div className="whitespace-pre-wrap leading-[1.6]">{mainMessage}</div>
        {expandableMessage && (
          <div
            className={`bg-dark-surface-secondary ${colors.border.primary}
            border ${effects.borderRadius.lg} mt-4 overflow-hidden`}
          >
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`w-full flex items-center justify-between
                  gap-1 p-4 ${typography.size.sm} ${isExpanded && "bg-black/20"}`}
            >
              <div className="flex items-center gap-4">
                <LuTriangleAlert
                  size={20}
                  className="text-[#D8C640]"
                />
                <span className={colors.text.secondary}>
                  {isExpanded ? "Hide details" : "Error details"}
                </span>
              </div>
              <LuChevronDown
                size={24}
                className={`${effects.transition.transform} ${
                  isExpanded ? "-rotate-180" : "rotate-0"
                }`}
              />
            </button>
            <div
              className={`overflow-y-auto overflow-x-hidden
                  transition-all duration-500 ease-in-out mx-4 ${
                    isExpanded
                      ? "max-h-[1000px] opacity-100 my-4"
                      : "max-h-0 opacity-0 my-0"
                  }`}
            >
              <div className={`flex items-start gap-4 w-full min-w-0 ${typography.size.sm}`}>
                <span className="break-words hyphens-auto whitespace-pre-wrap flex-1 min-w-0">
                  {parse(expandableMessage)}
                </span>

                <div className="pt-1 flex-shrink-0">
                  <CopyToClipboard
                    text={expandableMessage}
                    onCopy={() => {
                      showSnackbar("Copied to clipboard");
                    }}
                  >
                    <LuCopy
                      size={20}
                      className={`hover:cursor-pointer ${colors.hover.opacity} ${effects.transition.opacity}`}
                    />
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {rollbackBtn && (
        <button
          className={`flex items-center gap-1.5 px-3 py-1.5 -mt-5
          bg-dark-surface-secondary
          ${effects.borderRadius.md}
          ${colors.hover.brightness}
          ${effects.transition.default} ${typography.size.sm}
          ${colors.text.secondary}`}
          onClick={rollbackBtn.onClick}
          disabled={rollbackBtn.isDisabled}
        >
          <LuRotateCcw size={14} />
          <span>Revert this</span>
        </button>
      )}
    </div>
  );
}

function LazyImage({
  attachment,
  onClick,
}: {
  attachment: SharedMessageTypes.UserMessageAttachment;
  onClick?: () => void;
}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  const getSrc = () => {
    if (attachment.content) {
      return `data:image/png;base64,${attachment.content}`;
    } else if (attachment.url) {
      if (import.meta.env.DEV) {
        // Below attachment url is constructed for local development to allow Vite proxy to work
        return attachment.url.replace("https://dev.darvin.dev", "");
      }
      return attachment.url;
    }
    return undefined;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        threshold: 0.5,
      }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      onClick={onClick}
      className="relative max-w-[200px] cursor-pointer
    hover:opacity-75 transition-opacity duration-200"
    >
      {!isInView && (
        <div
          className="w-20 h-20 flex items-center justify-center
        rounded-sm border border-light-placeholder dark:border-dark-placeholder"
        >
          <LuLoaderCircle className="animate-spin" size={24} />
        </div>
      )}
      {isInView && (
        <img
          src={getSrc()}
          alt="Attached image"
          loading="lazy"
          style={{
            opacity: isInView ? 1 : 0,
            transition: "opacity 0.3s ease-in",
          }}
          className="max-h-28 w-auto rounded-sm
              border border-light-placeholder dark:border-dark-placeholder"
        />
      )}
    </div>
  );
}

function File({ id, type, name }: { id: string; type: string; name: string }) {
  return (
    <>
      <div
        className="w-[50px] h-[50px] overflow-hidden"
        data-tooltip-id={`file-tooltip-${id}`}
        data-tooltip-content={name}
      >
        <FileMosaic
          id={id}
          type={type}
          name={name}
          darkMode
          style={{ width: "50px", height: "50px", fontSize: "12px" }}
        />
      </div>
      <Tooltip id={`file-tooltip-${id}`} />
    </>
  );
}
